/**
 * Supply-an-image mode: a source file plus a written instruction.
 *
 * Fully controlled, like every other input on the page — the page owns `file` and
 * `instruction` so it can gate Generate and build the multipart body without reaching in here.
 * The one piece of local state is the object URL for the thumbnail, which is a rendering
 * concern derived from `file` and of no interest to the parent.
 *
 * The guardrail is not run here. The page runs the detector on the instruction and renders its
 * own gate beneath this component; this only reflects the outcome, so the field is visibly
 * tied to the warning panel.
 */

import ClearOutlined from "@mui/icons-material/ClearOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import { Box, Button, ButtonBase, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { ENHANCE_INSTRUCTION } from "@/constants/generate";
import { RADIUS_INNER_PX, RADIUS_SMALL_PX, jp } from "@/theme/tokens";

const PLACEHOLDER =
  "Describe the change you want: style, lighting, palette, mood or background. " +
  'e.g. "Restyle as a warm editorial portrait: softer side light, muted palette, ' +
  'shallow depth of field, keep the subject and composition as they are."';

const ACCEPT = "image/png,image/jpeg,image/webp";

/** Human-readable file size for the thumbnail caption. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface EditFormProps {
  file: File | null;
  /** `null` clears the selection. */
  onFileChange: (file: File | null) => void;
  instruction: string;
  onInstructionChange: (instruction: string) => void;
  disabled?: boolean;
  /** Warning-tones the field when the page's guardrail check has flagged this text. */
  guardrailFlagged?: boolean;
}

export function EditForm({
  file,
  onFileChange,
  instruction,
  onInstructionChange,
  disabled = false,
  guardrailFlagged = false,
}: EditFormProps) {
  /*
   * `createObjectURL` allocates a blob URL the browser keeps alive — holding the file in
   * memory — until it is explicitly revoked. So it is created in an effect keyed on `file`,
   * not inline during render which would leak one per render, and revoked in the cleanup:
   * at most one live URL at a time, released as soon as the file is replaced, cleared, or the
   * component unmounts.
   *
   * **The URL is stored with the file it belongs to**, and the rendered value is derived by
   * comparing the two. Clearing it with a `setPreviewUrl(null)` in the no-file branch would be
   * a synchronous setState inside an effect — a cascading render — and it would also be the
   * weaker check: this way a stale URL from a previous file simply does not match, whether or
   * not the effect has caught up.
   */
  const [preview, setPreview] = useState<{ file: File; url: string } | null>(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    /*
     * The one `set-state-in-effect` exception in this codebase, and it is the case the rule
     * exists to permit: allocating a resource outside React whose lifetime must be paired with
     * a commit. The alternatives are worse rather than merely different — creating the URL in
     * `useMemo` moves an allocation into render, where React may discard the result without
     * ever running a cleanup and leak the blob; a ref would hold the URL somewhere no render
     * reads. The cascading render the rule warns about is one, on picking a file.
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreview({ file, url });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const previewUrl = preview && preview.file === file ? preview.url : null;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
          Source image
        </Typography>
        {file ? (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              p: 2,
              mt: 0.5,
              border: `1px solid ${jp.line}`,
              backgroundColor: "rgba(255,255,255,0.8)",
            }}
          >
            <Box
              component="img"
              src={previewUrl ?? undefined}
              alt={`Selected source image: ${file.name}`}
              sx={{ height: 80, width: 80, flexShrink: 0, borderRadius: `${RADIUS_SMALL_PX}px`, objectFit: "cover" }}
            />
            {/* `minWidth: 0` is what lets `noWrap` actually clip inside a flex row. */}
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatBytes(file.size)}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ClearOutlined />}
              disabled={disabled}
              onClick={() => onFileChange(null)}
            >
              Remove
            </Button>
          </Stack>
        ) : (
          <Dropzone accept={ACCEPT} disabled={disabled} onFileSelect={onFileChange} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          PNG, JPEG or WebP, up to 10MB. The model uses this as the starting point.
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        <TextField
          id="edit-instruction"
          label="What should change?"
          multiline
          minRows={5}
          placeholder={PLACEHOLDER}
          helperText="Say what to change and what to keep. The image is the subject; this is the direction."
          value={instruction}
          disabled={disabled}
          error={guardrailFlagged}
          onChange={(event) => onInstructionChange(event.target.value)}
        />
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesomeOutlined />}
            disabled={disabled}
            onClick={() => onInstructionChange(ENHANCE_INSTRUCTION)}
          >
            Just enhance it
          </Button>
          <Typography variant="caption" color="text.secondary">
            Not sure what to write? Same photo, professional finish: nothing added, removed or
            changed about what&rsquo;s really there.
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

/**
 * Drag-and-drop or click to browse.
 *
 * Lives here rather than in a shared `components/ui`: it was a shared primitive in the Next.js
 * app and had exactly one caller, this form. A second caller is the moment to move it.
 */
function Dropzone({
  onFileSelect,
  accept,
  disabled,
}: {
  onFileSelect: (file: File) => void;
  accept: string;
  disabled: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);

  return (
    <ButtonBase
      component="div"
      disabled={disabled}
      onClick={() => inputEl?.click()}
      onDragOver={(event: React.DragEvent) => {
        event.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(false);
        if (disabled) return;
        const file = event.dataTransfer.files[0];
        if (file) onFileSelect(file);
      }}
      sx={{
        mt: 0.5,
        width: "100%",
        flexDirection: "column",
        gap: 1,
        px: 3,
        py: 5,
        borderRadius: `${RADIUS_INNER_PX}px`,
        border: "2px dashed",
        borderColor: dragOver ? jp.navy : jp.line,
        backgroundColor: dragOver ? "rgba(51,105,255,0.05)" : "rgba(255,255,255,0.8)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <ImageOutlined color="primary" />
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Drop your image here, or click to browse
      </Typography>
      <Typography variant="caption" color="text.secondary">
        PNG, JPEG or WebP
      </Typography>
      <Box
        component="input"
        ref={setInputEl}
        type="file"
        accept={accept}
        sx={{ display: "none" }}
        disabled={disabled}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const file = event.target.files?.[0];
          if (file) onFileSelect(file);
          // Reset so re-picking the same file still fires onChange.
          event.target.value = "";
        }}
      />
    </ButtonBase>
  );
}
